import { Icons, Menu, MenuItem } from '@dbrain/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useProductOn } from '../../api/license.js';
import { switchTarget, useCapabilities, useMeta, type Experience } from '../../experience.js';
import { LicenseChip } from '../license/LicenseChip.js';
import { NotificationBell } from '../NotificationBell.js';
import { PlanChip, ProBadge } from '../plan/PlanParts.js';
import { useShell } from './context.js';

/** The page path that makes sense on the other address: a conversation or workspace belongs to its own experience. */
function useSwitchPath(): string {
  const location = useLocation();
  return location.pathname.startsWith('/c/') || location.pathname.startsWith('/w/') ? '/' : `${location.pathname}${location.search}`;
}

/** One click to the other experience; one login covers both addresses. Hidden when the license does not have it (D-125). */
export function ExperienceSwitch({ experience, compact = false }: { experience: Experience; compact?: boolean }) {
  const { t } = useTranslation();
  const meta = useMeta();
  const capabilities = useCapabilities();
  const path = useSwitchPath();
  const otherOn = useProductOn(experience === 'chat' ? 'code' : 'chat');
  // Phase 16a: no Code in this edition, nothing to switch to
  if (!otherOn || !capabilities.codeExperience) return null;
  const label = experience === 'chat' ? t('experience.toCode') : t('experience.toChat');
  // a conversation's bar names the other product only (SoruKarti.dc.html); the name stays in the label
  return (
    <button
      type="button"
      className={compact ? 'ui-button ui-button--secondary ui-button--sm topbar__switch topbar__switch--compact' : 'ui-button ui-button--secondary topbar__switch'}
      onClick={() => window.location.assign(switchTarget(meta.data, experience, path))}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      data-testid="experience-switch"
    >
      {experience === 'chat' ? <Icons.code size={15} className="topbar__switch-icon--code" /> : <Icons.chat size={15} className="topbar__switch-icon--chat" />}
      <span>{compact ? t(`experience.${experience === 'chat' ? 'code' : 'chat'}`) : label}</span>
    </button>
  );
}

/** "CompanyGPT ⌄": which experience this is, with the other one a click away (when the license has it). */
export function ExperienceMenu({ experience }: { experience: Experience }) {
  const { t } = useTranslation();
  const meta = useMeta();
  const capabilities = useCapabilities();
  const path = useSwitchPath();
  const on = { chat: useProductOn('chat'), code: useProductOn('code') };
  // Phase 16a: one product, its plain name ("Chat"); a person's plan is on the right of the bar
  if (!capabilities.codeExperience) {
    return (
      <span className="topbar__brand topbar__brand--plain">
        <span data-testid="brand" data-experience={experience}>
          {t(`experience.${experience}`)}
        </span>
      </span>
    );
  }
  return (
    <Menu
      className="topbar__experience"
      triggerClassName="topbar__brand"
      label={t('shell.experience')}
      trigger={
        <>
          <span data-testid="brand" data-experience={experience}>
            {t(`experience.${experience}`)}
          </span>
          <Icons.chevronDown size={16} />
        </>
      }
    >
      {(['chat', 'code'] as const).filter((value) => value === experience || on[value]).map((value) => (
        <MenuItem
          key={value}
          icon={value === experience ? <Icons.check /> : value === 'chat' ? <Icons.conversations /> : <Icons.code />}
          aria-current={value === experience ? 'true' : undefined}
          hint={t(`shell.experienceHint.${value}`)}
          onClick={() => value !== experience && window.location.assign(switchTarget(meta.data, experience, path))}
        >
          {t(`experience.${value}`)}
        </MenuItem>
      ))}
    </Menu>
  );
}

/** The phone's menu button; hidden on wider screens. */
export function DrawerButton() {
  const { t } = useTranslation();
  const shell = useShell();
  return (
    <button type="button" className="ui-icon-button topbar__drawer" aria-label={t('shell.openMenu')} title={t('shell.openMenu')} onClick={shell.openDrawer} data-testid="drawer-open">
      <Icons.menu />
    </button>
  );
}

/**
 * The bar at the top of a page. By default the experience menu on the left
 * and the switch and notifications on the right; a page can put its own
 * title on the left (a conversation does).
 */
export function TopBar({ experience, left, right, showSwitch = true }: { experience: Experience; left?: ReactNode; right?: ReactNode; showSwitch?: boolean }) {
  return (
    <header className="topbar">
      <DrawerButton />
      <div className="topbar__left">{left ?? <ExperienceMenu experience={experience} />}</div>
      <div className="topbar__right">
        <LicenseChip />
        <PlanChip />
        <ProBadge />
        {right}
        {showSwitch ? <ExperienceSwitch experience={experience} /> : null}
        <NotificationBell />
      </div>
    </header>
  );
}
